<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useScheduleStore } from '@/stores/schedule.store'
import { infoBoxIdEnum } from '@/stores/portal.store'
import { useDetectDevice } from '@/composables/device'
import { useHighlightNewRow } from '@/composables/scroll'

import InfoBox from '@/components/shared/InfoBox.vue'
import TaskTypeEdit from '@/components/task/TaskTypeEdit.vue'

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const scheduleStore = useScheduleStore()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()

const container = ref(null)
const editType = ref(null)

const taskTypes = computed(() => scheduleStore.getActiveTaskTypes())

const fieldNames = (taskType) => taskType.extra_fields.map(field => field.name).join(', ')

const onTypeSaved = async(saved) => {
  const createdId = editType.value?.id ? null : saved?.id
  editType.value = null
  if (createdId) await highlightRowAsync(createdId, container)
}

const moveAsync = async(index, offset) => {
  const ids = taskTypes.value.map(taskType => taskType.id)
  ids.splice(index + offset, 0, ...ids.splice(index, 1))
  await scheduleStore.reorderTaskTypesAsync(ids)
  await scheduleStore.fetchTaskTypesAsync()
}

onMounted(scheduleStore.ensureTaskTypesAsync)
</script>

<template>
  <div ref="container" class="task-type-settings">
    <div class="row justify-end items-center q-pb-sm" :class="{ 'q-px-md': isMobile }">
      <q-btn
        style="padding: 0;"
        size="md"
        unelevated
        ripple
        rounded
        icon="mdi-plus-circle-outline"
        @click="editType = {}"
      >
        <q-tooltip>{{ i18n.t('common.tasks.newTaskType') }}</q-tooltip>
      </q-btn>
    </div>
    <q-list bordered separator class="rounded-borders">
      <q-item
        v-for="(taskType, index) in taskTypes"
        :key="taskType.id"
        class="q-py-sm"
        :class="{ 'row-highlight': taskType.id === highlightedRowId }"
        clickable
        @click="editType = taskType"
      >
        <q-item-section avatar>
          <q-avatar
            size="md"
            :icon="taskType.icon"
            text-color="white"
            :style="{ backgroundColor: taskType.color }"
          />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-weight-medium">{{ taskType.name }}</q-item-label>
          <q-item-label caption>{{ fieldNames(taskType) || i18n.t('common.extraFields.empty') }}</q-item-label>
        </q-item-section>
        <q-item-section side>
          <div class="row no-wrap items-center">
            <q-btn
              flat
              round
              dense
              size="sm"
              icon="mdi-arrow-up"
              :disable="index === 0"
              @click.stop="moveAsync(index, -1)"
            />
            <q-btn
              flat
              round
              dense
              size="sm"
              icon="mdi-arrow-down"
              :disable="index === taskTypes.length - 1"
              @click.stop="moveAsync(index, 1)"
            />
            <q-btn
              flat
              round
              dense
              size="sm"
              icon="mdi-pencil"
              @click.stop="editType = taskType"
            >
              <q-tooltip>{{ i18n.t('common.edit') }}</q-tooltip>
            </q-btn>
          </div>
        </q-item-section>
      </q-item>
      <q-item v-if="!taskTypes.length">
        <q-item-section class="text-grey-7">{{ i18n.t('common.tasks.noTaskTypes') }}</q-item-section>
      </q-item>
    </q-list>
    <info-box :id="infoBoxIdEnum.TASK_TYPES" :class="isMobile ? 'q-ma-md' : 'q-mt-md'" :message="i18n.t('info.taskTypes')" />

    <q-dialog
      :model-value="editType !== null"
      :maximized="isMobile"
      class="dialog-xs"
      @hide="editType = null"
    >
      <task-type-edit
        v-if="editType !== null"
        :model-value="editType"
        @update="onTypeSaved"
        @cancel="editType = null"
      />
    </q-dialog>
  </div>
</template>

<style scoped>
.task-type-settings {
  max-width: 40rem;
}
</style>
